/**
 * Arma el gráfico "Type visits" / pacientes del cierre desde `patients_by_type`.
 * No suma memberships.NP/NC: la membresía ya viene como tipo M/B en el conteo.
 */

const REGULAR_ORDER = [
  { prefix: 'F/U', short: 'F/U', long: 'Follow Up' },
  { prefix: 'N/C', short: 'N/C', long: 'New Consultation' },
  { prefix: 'N/P', short: 'N/P', long: 'New patient' },
  { prefix: 'P/I', short: 'P/I', long: 'Pending Injection' },
  { prefix: 'P/P', short: 'P/P', long: 'Pending Procedure' },
  { prefix: 'R/S', short: 'R/S', long: 'Result' },
  { prefix: 'P/S', short: 'P/S', long: 'Pregnancy Screening' },
  { prefix: 'V/C', short: 'Vacc', long: 'COVID-19 Vaccination' },
  { prefix: 'M/B', short: 'M/B', long: 'Membership' },
  { prefix: 'IV/NP', short: 'IV/NP', long: 'IV therapies - NP' },
  { prefix: 'IV/NC', short: 'IV/NC', long: 'IV therapies - NC' },
]

const INSURANCE_ORDER = [
  { prefix: 'INS-NP', short: 'INS-NP', long: 'Insurance NP' },
  { prefix: 'INS-NC', short: 'INS-NC', long: 'Insurance NC' },
  { prefix: 'INS-F/U', short: 'INS-F/U', long: 'Insurance Follow Up' },
  { prefix: 'INS-R/S', short: 'INS-R/S', long: 'Insurance Result' },
]

/**
 * @param {Array<{ prefix?: string, name?: string, count?: number }>} patientsByType
 * @param {{ shortLabels?: boolean, lane?: 'regular' | 'insurance' | 'all' }} [options]
 * @returns {{ labels: string[], series: number[], total: number, countsByPrefix: Record<string, number> }}
 */
export function chartFromPatientsByType(patientsByType, options = {}) {
  const shortLabels = !!options.shortLabels
  const lane = options.lane || 'regular'
  const order =
    lane === 'insurance'
      ? INSURANCE_ORDER
      : REGULAR_ORDER

  const countsByPrefix = Object.create(null)
  ;(patientsByType || []).forEach(row => {
    const prefix = row && row.prefix
    if (!prefix) return
    // En dashboard general / regular no mezclar tipos de seguro
    if (lane !== 'insurance' && String(prefix).startsWith('INS-')) return
    if (lane === 'insurance' && !String(prefix).startsWith('INS-')) return
    countsByPrefix[prefix] =
      (countsByPrefix[prefix] || 0) + (parseInt(row.count, 10) || 0)
  })

  const labels = []
  const series = []
  order.forEach(item => {
    const count = countsByPrefix[item.prefix] || 0
    // Mostrar siempre los tipos del carril (incluye 0) para alinear con el contrato
    const label = shortLabels ? item.short : item.long
    labels.push(`${label}: ${count}`)
    series.push(count)
  })

  const total = series.reduce((sum, n) => sum + n, 0)
  return { labels, series, total, countsByPrefix }
}

/**
 * Fallback: suma year_data por prefix sin membresías ni INS en carril regular.
 * @param {Array} yearData
 * @param {{ shortLabels?: boolean, lane?: 'regular' | 'insurance' | 'all' }} [options]
 */
export function chartFromYearData(yearData, options = {}) {
  const shortLabels = !!options.shortLabels
  const lane = options.lane || 'regular'
  const order = lane === 'insurance' ? INSURANCE_ORDER : REGULAR_ORDER
  const countsByPrefix = Object.create(null)
  order.forEach(item => {
    countsByPrefix[item.prefix] = 0
  })

  ;(yearData || []).forEach(month => {
    ;((month && month.facilities) || []).forEach(facility => {
      order.forEach(item => {
        countsByPrefix[item.prefix] += parseInt(facility[item.prefix], 10) || 0
      })
    })
  })

  const labels = []
  const series = []
  order.forEach(item => {
    const count = countsByPrefix[item.prefix] || 0
    labels.push(`${shortLabels ? item.short : item.long}: ${count}`)
    series.push(count)
  })

  const total = series.reduce((sum, n) => sum + n, 0)
  return { labels, series, total, countsByPrefix }
}
